import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Drawer, Input, Switch, TextField } from "@heroui/react";
import { helpSettingsStore } from "@/stores/HelpSettingsStore";
import HelpPhoneField from "@/pages/HelpPage/components/HelpPhoneField";
import {
  clampExperienceYears,
  MAX_EXECUTOR_EXPERIENCE_YEARS,
} from "@/pages/HelpPage/helpMappers";

const ExecutorFormDrawer: React.FC = observer(() => {
  const store = helpSettingsStore;
  const {
    executorFormOpen,
    isExecutorEditing,
    executorEditingUuid,
    executorForm,
    executorFormErrors,
    executorSubmitAttempted,
    executorSubmitting,
  } = store;

  const fieldError = (field: string) =>
    executorSubmitAttempted ? executorFormErrors[field] : undefined;

  const [experienceText, setExperienceText] = useState("");

  useEffect(() => {
    if (!executorFormOpen) return;
    setExperienceText(
      executorForm.experienceYears > 0
        ? String(executorForm.experienceYears)
        : "",
    );
  }, [executorFormOpen, isExecutorEditing, executorEditingUuid]);

  const syncExperienceToStore = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === "") {
      store.setExecutorField("experienceYears", 0);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed)) {
      store.setExecutorField("experienceYears", clampExperienceYears(parsed));
      setExperienceText(String(clampExperienceYears(parsed)));
    }
  };

  return (
    <Drawer.Backdrop
      isOpen={executorFormOpen}
      onOpenChange={(open) => {
        if (!open) store.requestCloseExecutorForm();
      }}
      className="help-view-backdrop"
    >
      <Drawer.Content placement="right" className="help-view-content">
        <Drawer.Dialog className="help-view-dialog" data-testid="help-settings-executor-drawer">
          <Drawer.CloseTrigger data-testid="help-settings-executor-drawer-close" />
          <Drawer.Header className="help-view-header">
            <Drawer.Heading>
              {isExecutorEditing
                ? "Редактирование исполнителя"
                : "Новый исполнитель"}
            </Drawer.Heading>
            <p className="help-view-subtitle">
              Появится в списке для назначения в заявке
            </p>
          </Drawer.Header>

          <Drawer.Body className="help-view-body help-settings-drawer-body">
            <div className="help-settings-form">
              <div className="help-settings-form-row help-settings-form-row--2col">
                <div className="help-form-field help-settings-field--w242 help-settings-input--h36">
                  <span className="help-form-field__label">Имя *</span>
                  <TextField fullWidth isInvalid={!!fieldError("name")}>
                    <Input
                      value={executorForm.name}
                      onChange={(e) =>
                        store.setExecutorField("name", e.target.value)
                      }
                    />
                  </TextField>
                  {fieldError("name") ? (
                    <span className="help-form-field__error">
                      {fieldError("name")}
                    </span>
                  ) : null}
                </div>

                <div className="help-form-field help-settings-field--w242 help-settings-input--h36">
                  <span className="help-form-field__label">Телефон *</span>
                  <HelpPhoneField
                    value={executorForm.phone}
                    isInvalid={!!fieldError("phone")}
                    onChange={(value) => store.setExecutorField("phone", value)}
                  />
                  {fieldError("phone") ? (
                    <span className="help-form-field__error">
                      {fieldError("phone")}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="help-settings-form-row help-settings-form-row--4col">
                <div className="help-form-field help-settings-field--w115 help-settings-input--h36">
                  <span className="help-form-field__label">Авто</span>
                  <TextField fullWidth>
                    <Input
                      value={executorForm.vehicleModel}
                      onChange={(e) =>
                        store.setExecutorField("vehicleModel", e.target.value)
                      }
                    />
                  </TextField>
                </div>

                <div className="help-form-field help-settings-field--w115 help-settings-input--h36">
                  <span className="help-form-field__label">Госномер</span>
                  <TextField fullWidth>
                    <Input
                      value={executorForm.vehiclePlate}
                      onChange={(e) =>
                        store.setExecutorField("vehiclePlate", e.target.value)
                      }
                    />
                  </TextField>
                </div>

                <div className="help-form-field help-settings-field--w115 help-settings-input--h36">
                  <span className="help-form-field__label">Компания</span>
                  <TextField fullWidth>
                    <Input
                      value={executorForm.company}
                      onChange={(e) =>
                        store.setExecutorField("company", e.target.value)
                      }
                    />
                  </TextField>
                </div>

                <div className="help-form-field help-settings-field--w115 help-settings-input--h36">
                  <span className="help-form-field__label">Стаж, лет</span>
                  <TextField fullWidth isInvalid={!!fieldError("experienceYears")}>
                    <Input
                      inputMode="numeric"
                      value={experienceText}
                      onChange={(e) => {
                        const next = e.target.value.replace(/[^\d]/g, "");
                        setExperienceText(next);
                        if (next === "") {
                          store.setExecutorField("experienceYears", 0);
                          return;
                        }
                        const parsed = Number(next);
                        if (!Number.isNaN(parsed)) {
                          store.setExecutorField("experienceYears", parsed);
                        }
                      }}
                      onBlur={() => syncExperienceToStore(experienceText)}
                    />
                  </TextField>
                  <span className="help-form-field__hint">
                    максимум {MAX_EXECUTOR_EXPERIENCE_YEARS}
                  </span>
                  {fieldError("experienceYears") ? (
                    <span className="help-form-field__error">
                      {fieldError("experienceYears")}
                    </span>
                  ) : null}
                </div>
              </div>

              <label className="help-switch-row help-settings-switch-row">
                В списке заявок
                <Switch
                  className="fbs-switch"
                  isSelected={executorForm.enabled}
                  onChange={(checked) =>
                    store.setExecutorField("enabled", checked)
                  }
                >
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch>
              </label>
            </div>
          </Drawer.Body>

          <Drawer.Footer className="help-settings-footer">
            <Button
              variant="tertiary"
              onPress={() => store.requestCloseExecutorForm()}
              data-testid="help-settings-executor-cancel"
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onPress={() => void store.submitExecutor()}
              isPending={executorSubmitting}
              data-testid="help-settings-executor-submit"
            >
              Сохранить
            </Button>
          </Drawer.Footer>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
});

ExecutorFormDrawer.displayName = "ExecutorFormDrawer";
export default ExecutorFormDrawer;
